// Cave84 — Munich Wine Company invoice parser (pure; no React, no Firebase).
//
// Single source of truth for turning MWC invoice text into lots, allocating
// landed cost from the invoice's own totals, and reconciling the parse against
// those totals. Used by:
//   - apps/web/src/pages/InvoiceImportPage.tsx (browser import, via utils/invoiceSuppliers.ts)
//   - scripts/reconcile-mwc-invoices.ts      (historical invoice audit)
// Keep it free of browser/Firebase imports so Node can load it via tsx.
// The reconciliation helpers (mk, round2) and inferStyle are also used by the
// other supplier parsers (utils/tomerGalInvoiceParser.ts).

import type { WineStyle, AppellationInference } from '@cave84/shared-types';
import { inferAppellation, CHAMPAGNE_HOUSES, CHAMPAGNE_CUVEES } from '@cave84/shared-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ParsedLot {
  article: string;          // "36700-18"
  lot: number;              // 149
  rawWineName: string;      // "Cathiard, Sylvain 2003, Vosne Romanee 1er Cru Aux Malconsorts"
  qty: number;              // number of bottles in the lot
  inOwc: boolean;           // true when the lot is a full original wooden case (OHK/OWC)
  inGiftBox: boolean;       // true when sold "in Geschenkkarton" / "GK" (gift carton)
  lotPriceEur: number;      // hammer price for the whole lot
  pricePerBottleEur: number;// landed cost per bottle, allocated from invoice totals
  // parsed components
  producerRaw: string;      // as on invoice (may be "Cathiard, Sylvain")
  producerName: string;     // normalised ("Sylvain Cathiard")
  year: number;
  cuvee: string;            // "Vosne Romanee 1er Cru Aux Malconsorts"
  inferredStyle: WineStyle;
  /** Appellation derived from the cuvée at parse time (MWC invoices carry no
   *  structured appellation). `confidence: 'low'` is surfaced in the review
   *  step for the owner to confirm before import. */
  inferredAppellation: AppellationInference;
}

export interface InvoiceMeta {
  invoiceNumber: string;   // "55640"
  invoiceDate: string;     // "21.05.2026"
  bidder: string;
}

/** The totals block on the last page of an MWC invoice. `null` = not found. */
export interface InvoiceTotals {
  lotCount: number | null;      // "31 x Lot fee"           → 31
  lotFeeTotal: number | null;   // "31 x Lot fee 155,00 €"  → 155
  hammerTotal: number | null;   // "Hammer total: 7.410,00 €"
  premiumRate: number | null;   // "15% buyer's premium"    → 0.15
  premiumTotal: number | null;  // → 1111.50
  shipping: number | null;      // "Shipping 96,00 €"
  assurance: number | null;     // "Assurance: 37,05 €"
  nettoFees: number | null;     // "Netto fees: 1.399,55 €"
  vatRate: number | null;       // "19% VAT"                → 0.19
  vatTotal: number | null;      // → 265.92
  feesTotal: number | null;     // "Fees total: 1.665,47 €"
  billingSum: number | null;    // "Billing Sum: 9.075,47 €"
  /** Σ of fee lines outside the standard set, all inside "Netto fees" and the
   *  VAT base: "Handling 8,50 €", "Auktionsgebühr: 5,00 €", and credits such as
   *  "Versandguthaben … -10,00 €". 0 when none. */
  otherFees: number;
  otherFeeLines: string[];
}

export interface ReconCheck {
  id: string;
  label: string;
  /** Value printed on the invoice. `null` when the field could not be read. */
  invoice: number | null;
  /** Value derived from the parsed lots / recomputed arithmetic. */
  computed: number;
  delta: number;
  ok: boolean;
  severity: 'error' | 'warning';
  /** 'eur' = MWC amounts; 'money' = amounts in the invoice's own currency. */
  unit: 'count' | 'eur' | 'money';
  hint?: string;
}

export interface Reconciliation {
  checks: ReconCheck[];
  /** true when at least one `error`-severity check failed → block import */
  hasErrors: boolean;
  /** true when at least one `warning`-severity check failed */
  hasWarnings: boolean;
}

// ---------------------------------------------------------------------------
// PDF parser
// ---------------------------------------------------------------------------

// MWC invoice line regex:
//   article (36700-18)  lot#  wine-text  qty  Flasche(n)  [packaging]  price,00 €
//
// Group 5 is a tolerant "packaging" slot for whatever MWC prints between the
// bottle count and the price. Observed variants:
//   "2 Flaschen 360,00 €"                    → ""
//   "6 Flaschen OHK 480,00 €"                → "OHK"     (Original-Holzkiste / OWC)
//   "1 Flasche in 200,00 €" + "Geschenkkarton" wrapped to the next line → "in"
// The previous regex required `\s+` straight into the price, so every
// "… in Geschenkkarton" lot silently vanished from the import (4 lots on
// invoice 56283). Accept any short run of non-digit, non-€, same-line text.
const LINE_RE =
  /(\d{5}-\d+)\s+(\d+)\s+(.+?)\s+(\d+)\s+Flaschen?\b([^\d€\n]{0,40}?)\s*([\d.]+,\d{2})\s*€/g;

// English and German invoice layouts (MWC issued German invoices until ~2025-04:
// "Rechnung 52400 … Bieter: 306 … Datum: 11.04.2025").
const INVOICE_NUM_RE = /(?:Invoice|Rechnung)\s+(\d+)/i;
const DATE_RE = /(?:Date|Datum):?\s+(\d{2}\.\d{2}\.\d{4})/i;
const BIDDER_RE = /(?:Bidder|Bieter):\s*(\d+)/i;

// -- Totals block (last page) -----------------------------------------------
// Each pattern accepts the English label and its German equivalent.
const T_LOT_FEE     = /(\d+)\s*x\s*Lot\s*(?:fee|gebühren?):?\s+([\d.]+,\d{2})\s*€/i;
const T_HAMMER      = /(?:Hammer\s*total|Zuschlagssumme):?\s*([\d.]+,\d{2})\s*€/i;
const T_PREMIUM     = /(\d+(?:[.,]\d+)?)\s*%\s*(?:buyer'?s\s*premium|Aufgeld):?\s+([\d.]+,\d{2})\s*€/i;
// "Shipping 96,00 €" — must not match the "Shipping: HEFTOL GROUP, …" footer line.
const T_SHIPPING    = /Shipping\s+([\d.]+,\d{2})\s*€/i;
const T_ASSURANCE   = /Assurance:?\s*([\d.]+,\d{2})\s*€/i;
// German "Summe: 59,00 €" — \b keeps it off "Zwischensumme"/"Zuschlagssumme",
// and the colon keeps it off "Summe Gebühren".
const T_NETTO_FEES  = /(?:Netto\s*fees:?|\bSumme:)\s*([\d.]+,\d{2})\s*€/i;
const T_VAT         = /(\d+(?:[.,]\d+)?)\s*%\s*(?:VAT|MwSt):?\s+([\d.]+,\d{2})\s*€/i;
const T_FEES_TOTAL  = /(?:Fees\s*total|Summe\s*Gebühren):?\s*([\d.]+,\d{2})\s*€/i;
const T_BILLING_SUM = /(?:Billing\s*Sum|Rechnungsbetrag):?\s*([\d.]+,\d{2})\s*€/i;
// Non-standard fee lines. All sit inside "Netto fees" and the VAT base.
const T_OTHER_FEES  = [
  /(Handling):?\s+(-?[\d.]+,\d{2})\s*€/gi,
  /(Auktionsgeb(?:ü|ue)hr|Auction\s*fee):?\s+(-?[\d.]+,\d{2})\s*€/gi,
  /(Versandguthaben[^\n€]*?)\s+(-[\d.]+,\d{2})\s*€/gi,  // shipping credit
];

/** Parse "400,00" / "7.410,00" → 400 / 7410 */
function parseEur(s: string): number {
  return parseFloat(s.replace(/\./g, '').replace(',', '.'));  // keeps a leading "-"
}

function eurOrNull(m: RegExpMatchArray | null, group = 1): number | null {
  return m?.[group] ? parseEur(m[group]) : null;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function parseInvoiceTotals(text: string): InvoiceTotals {
  const lotFee  = T_LOT_FEE.exec(text);
  const premium = T_PREMIUM.exec(text);
  const vat     = T_VAT.exec(text);
  const otherFeeLines: string[] = [];
  let otherFees = 0;
  for (const re of T_OTHER_FEES) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      otherFees += parseEur(m[2]!);
      otherFeeLines.push(`${m[1]!.trim()} ${m[2]}`);
    }
  }
  // "0 x Lot fee 0,00 €" = fee waived on that sale — the invoice then states no
  // lot count at all, so report it as unknown instead of "0 lots expected".
  const lotCountRaw = lotFee?.[1] ? parseInt(lotFee[1], 10) : null;
  return {
    lotCount:     lotCountRaw ? lotCountRaw : null,
    lotFeeTotal:  eurOrNull(lotFee, 2),
    hammerTotal:  eurOrNull(T_HAMMER.exec(text)),
    premiumRate:  premium?.[1] ? parseFloat(premium[1].replace(',', '.')) / 100 : null,
    premiumTotal: eurOrNull(premium, 2),
    shipping:     eurOrNull(T_SHIPPING.exec(text)),
    assurance:    eurOrNull(T_ASSURANCE.exec(text)),
    nettoFees:    eurOrNull(T_NETTO_FEES.exec(text)),
    vatRate:      vat?.[1] ? parseFloat(vat[1].replace(',', '.')) / 100 : null,
    vatTotal:     eurOrNull(vat, 2),
    feesTotal:    eurOrNull(T_FEES_TOTAL.exec(text)),
    billingSum:   eurOrNull(T_BILLING_SUM.exec(text)),
    otherFees:    round2(otherFees),
    otherFeeLines,
  };
}

// -- Packaging annotations ---------------------------------------------------

/**
 * MWC appends packaging notes to the bottle-count cell — "OHK" (Original-
 * Holzkiste), "OWC", "GK" / "in Geschenkkarton" (gift carton). pdfjs wraps the
 * long ones onto the following line, where the continuation-join heuristic
 * splices them into the wine name. Left in place they pollute `cuvee` and
 * create duplicate wines ("… Riesling Auslese GK" vs "… Riesling Auslese").
 * Strip them out and return them as structured flags instead.
 */
const PACKAGING_STRIP_RE =
  /\s*\b(?:in\s+|im\s+)?(OHK|OWC|GK|Geschenkkartons?|Geschenkpackung|Original-?\s?Holzkiste|Holzkiste)\b\.?/gi;

function extractPackaging(s: string): { clean: string; tokens: string[] } {
  const tokens: string[] = [];
  const clean = s
    .replace(PACKAGING_STRIP_RE, (_full, tok: string) => {
      tokens.push(tok.toUpperCase());
      return ' ';
    })
    // A bare trailing "in"/"im" is the orphaned preposition of a wrapped
    // "in Geschenkkarton" — drop it too.
    .replace(/\s+(?:in|im)\s*$/i, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return { clean, tokens };
}

const isOwcToken = (t: string) => /^(OHK|OWC|ORIGINAL-?\s?HOLZKISTE|HOLZKISTE)$/.test(t);
const isGiftToken = (t: string) => /^(GK|GESCHENKKARTONS?|GESCHENKPACKUNG)$/.test(t);

/**
 * MWC writes producers "Surname, Firstname" and appends a sub-estate as a third
 * comma part: "Müller, Egon, Le Gallais".
 *   "Cathiard, Sylvain"        → "Sylvain Cathiard"
 *   "Prüm, J. J."              → "J. J. Prüm"
 *   "Müller, Egon, Le Gallais" → "Egon Müller, Le Gallais"   (was: "Egon, Le Gallais Müller")
 *   "Ch. Rayas"                → "Ch. Rayas"
 */
function normalizeProducerName(raw: string): string {
  const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length < 2) return raw.trim();
  const [surname, forename, ...rest] = parts as [string, string, ...string[]];
  const head = `${forename} ${surname}`;
  return rest.length > 0 ? `${head}, ${rest.join(', ')}` : head;
}

/**
 * "Cathiard, Sylvain 2003, Vosne Romanee 1er Cru Aux Malconsorts"
 *   → { producer: "Cathiard, Sylvain", year: 2003, cuvee: "Vosne Romanee 1er Cru Aux Malconsorts" }
 */
/** App-wide convention for a non-vintage wine (see ImportWizardPage). */
export const NV_YEAR = 1001;

function parseWineName(raw: string): { producer: string; year: number; cuvee: string } | null {
  // Match the first 4-digit year (1900-2099), or "NV" for non-vintage
  // ("Vega Sicilia NV, Unico Reserva Especial"). Before NV support these lots
  // were dropped silently (invoice 53019 lost 2 lots / €590 hammer).
  const m = raw.match(/^(.+?)\s+((?:19|20)\d{2}|NV|N\.V\.),?\s*(.*)$/);
  if (!m) return null;
  const isNv = /^N\.?V\.?$/i.test(m[2]!);
  return {
    producer: m[1]!.trim(),
    year: isNv ? NV_YEAR : parseInt(m[2]!),
    cuvee: m[3]!.trim(),
  };
}

// Style inference. MWC invoices carry no style column, and the wine_analysis
// Cloud Function never writes `style` — this function is the ONLY source of
// truth for it, so a miss is permanent until edited by hand. The previous
// version keyed on ~8 Burgundy words, which silently filed every Champagne and
// every Mosel Riesling as `red`.
//
// Order matters: fortified → sparkling → sweet → rosé → white → red (default).
const RE_FORTIFIED = /\b(port|porto|madeira|madère|sherry|jerez|oloroso|amontillado|fino|manzanilla|palo cortado|pedro xim[eé]nez|\bpx\b|banyuls|maury|rivesaltes|marsala|vin doux naturel|vdn)\b/i;

// Champagne house/cuvée lists are owned by appellation-inference.ts — style and
// appellation must never disagree about whether a bottle is Champagne.
const RE_SPARKLING_OTHER = /\b(brut|cr[eé]mant|franciacorta|prosecco|cava|spumante|sekt|metodo classico|m[eé]thode traditionnelle|champagne)\b/i;

// Prädikat "Auslese and above" is edelsüß; Kabinett/Spätlese stay `white`.
const RE_SWEET = /\b(aszu|asz[uú]|eszencia|essencia|tokaji|tokay|puttonyos|buttonyos|auslese|beerenauslese|trockenbeerenauslese|\btba\b|\bba\b|eiswein|icewine|ice wine|sauternes|barsac|yquem|c[eé]rons|loupiac|sainte-?croix-?du-?mont|vendanges? tardives?|s[eé]lection de grains nobles|\bsgn\b|moelleux|liquoreux|botrytis|noble rot|late harvest|vin santo|passito|recioto|maury blanc)\b/i;

const RE_ROSE = /\b(ros[eé]|rosado|rosato|tavel|[oe]il de perdrix)\b/i;

const RE_WHITE = /\b(blanc|bianco|blanco|wei[sß]|montrachet|b[aâ]tard|bienvenues|criots|chevalier|chablis|meursault|chassagne|puligny|corton[- ]charlemagne|charlemagne|riesling|chardonnay|sauvignon|chenin|vouvray|savenni[eè]res|montlouis|condrieu|ch[aâ]teau ?grillet|sancerre|pouilly[- ]fum[eé]|pouilly[- ]fuiss[eé]|muscadet|gr[uü]ner|veltliner|silvaner|scheurebe|wei[sß]burgunder|gew[uü]rztraminer|pinot gris|pinot grigio|viognier|marsanne|roussanne|s[eé]millon|albari[nñ]o|verdejo|macabeo|viura|godello|arneis|gavi|cortese|soave|garganega|vermentino|fiano|greco|falanghina|assyrtiko|furmint|hárslevel[uű]|harslevelu|kabinett|sp[aä]tlese|feinherb|trocken|halbtrocken)\b/i;

/** Lowercase + strip diacritics, to match the shared inference tables. */
function foldForMatch(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function inferStyle(cuvee: string, producerName = ''): WineStyle {
  const s = `${producerName} ${cuvee}`;
  const folded = foldForMatch(s);
  if (RE_FORTIFIED.test(s)) return 'fortified';
  if (RE_SPARKLING_OTHER.test(s) ||
      CHAMPAGNE_HOUSES.test(folded) ||
      CHAMPAGNE_CUVEES.test(folded)) return 'sparkling';
  if (RE_SWEET.test(s)) return 'sweet';
  if (RE_ROSE.test(s)) return 'rose';
  if (RE_WHITE.test(s)) return 'white';
  return 'red';
}

/** A pdfjs text item reduced to what the row-grouping needs. */
export interface PdfTextItem { x: number; y: number; str: string }

/**
 * Rebuild visual lines from one page's pdfjs text items: group by y (±4px),
 * rows top-to-bottom, items left-to-right. Pure — shared by the browser import
 * page and the Node reconciliation script so both see identical text.
 */
export function pdfItemsToPageText(pageItems: PdfTextItem[]): string {
  const items = pageItems
    .filter(it => it.str.trim() !== '')
    .map(it => ({ ...it, y: Math.round(it.y) }));

  // Group by y (±4px tolerance)
  const rows = new Map<number, PdfTextItem[]>();
  for (const item of items) {
    let key = -1;
    for (const k of rows.keys()) {
      if (Math.abs(k - item.y) <= 4) { key = k; break; }
    }
    if (key === -1) { key = item.y; rows.set(key, []); }
    rows.get(key)!.push(item);
  }

  // Sort rows top-to-bottom (highest y first in PDF coords), items left-to-right
  return Array.from(rows.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([, row]) => row.sort((a, b) => a.x - b.x).map(it => it.str).join(' '))
    .join('\n');
}

/** pdfjs `getTextContent().items` → PdfTextItem[] (drops marked-content items). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toPdfTextItems(contentItems: any[]): PdfTextItem[] {
  return contentItems
    .filter(it => 'str' in it && typeof it.str === 'string')
    .map(it => ({ x: it.transform[4] as number, y: it.transform[5] as number, str: it.str as string }));
}

export interface ParsedInvoice {
  lots: ParsedLot[];
  /** Lot lines matched but not parseable into producer/year/cuvée. */
  unparsedLines: string[];
  meta: InvoiceMeta;
  totals: InvoiceTotals;
  reconciliation: Reconciliation;
}

/**
 * Text → lots. Split out from `parseMwcPdf` so the line-joining, regex and
 * reconciliation logic is unit-testable without a real PDF.
 */
export function parseMwcInvoiceText(text: string): ParsedInvoice {
  const meta: InvoiceMeta = {
    invoiceNumber: INVOICE_NUM_RE.exec(text)?.[1] ?? '',
    invoiceDate:   DATE_RE.exec(text)?.[1] ?? '',
    bidder:        BIDDER_RE.exec(text)?.[1] ?? '',
  };

  // Read the totals block off the RAW text — the continuation-join below
  // deliberately discards these lines as footer noise.
  const totals = parseInvoiceTotals(text);

  // Join continuation lines. Three cases for a line that doesn't start a new lot:
  //   (a) Wine name overflow  → a single short word/phrase (e.g. "Malconsorts") that
  //       pdfjs placed after the price; insert it BEFORE the qty/price.
  //   (b) Footer / boilerplate → discard.
  //   (c) Lot already complete (has qty+price) + line is not wine overflow → discard.
  //
  // "New lot" detection: article numbers like "36700-32" may be preceded by a short
  // PDF margin marker (e.g. "—LF "), so we look within the first 6 chars too.
  // Mirrors LINE_RE's tolerant packaging slot — must stay in sync, otherwise a
  // "1 Flasche in 200,00 €" line looks "incomplete" and the overflow word gets
  // appended after the price instead of being spliced into the wine name.
  const QTY_SUFFIX_RE = /(\s+\d+\s+Flaschen?\b[^\d€\n]{0,40}?\s*[\d.]+,\d{2}\s*€.*)$/;
  const FOOTER_RE = /Sub total|Shipping|Billing|VAT|@|\.com|\.de|\bGmbH\b|payment|property of|due for|service period|Page:|Invoice|Jäger|Deisenhofen|\+49|\bFax\b|www\.|Rechnung|Zwischensumme|Seite\b|Bankverbindung|Kreissparkasse|Geschäftsführer|Lieferadresse|Eigentum|Zahlung/i;
  // The invoice's table column-header row, repeated at the top of each page.
  // pdfjs emits it as a short, digit-free line ("Article Lot Wine Bottles Price")
  // that the wine-overflow heuristic below would otherwise splice into the
  // previous lot's cuvee. Discard it explicitly.
  const HEADER_RE = /^(?:Article\s+Lot\s+Wine\s+Bottles?\s+Price|Artikel\s+Lot\s+Wein\s+Anzahl\s+Preis)$/i;
  const isLotStart = (s: string) =>
    /^\d{5}-\d+/.test(s) || /^.{1,6}\d{5}-\d+/.test(s);

  const joinedText = text
    .split('\n')
    .reduce<string[]>((acc, line) => {
      const trimmed = line.trim();
      if (!trimmed) return acc;

      if (isLotStart(trimmed) || acc.length === 0) {
        acc.push(trimmed);
        return acc;
      }

      // Discard known footer/boilerplate patterns + repeated table header row
      if (FOOTER_RE.test(trimmed) || HEADER_RE.test(trimmed)) return acc;

      const prev = acc[acc.length - 1]!;
      const qtyMatch = QTY_SUFFIX_RE.exec(prev);

      // Wine-name overflow: short, no digits (e.g. "Malconsorts", "Suchots")
      const isWineOverflow = trimmed.length < 35 && !/\d/.test(trimmed);

      if (qtyMatch && isWineOverflow) {
        // Insert before qty/price so the regex captures the full wine name
        acc[acc.length - 1] =
          prev.slice(0, qtyMatch.index) + ' ' + trimmed + qtyMatch[0];
      } else if (!qtyMatch) {
        // Lot line not yet complete → append normally
        acc[acc.length - 1] += ' ' + trimmed;
      }
      // else: lot is complete + not wine overflow → discard (footer noise)

      return acc;
    }, [])
    .join('\n');

  const lots: ParsedLot[] = [];
  const seenArticles = new Set<string>();
  // Lot lines the regex matched but parseWineName could not split. Surfaced
  // instead of dropped silently; the lot-count / hammer checks also catch them.
  const unparsedLines: string[] = [];
  let m: RegExpExecArray | null;

  LINE_RE.lastIndex = 0;
  while ((m = LINE_RE.exec(joinedText)) !== null) {
    const article    = m[1]!;
    const lot        = parseInt(m[2]!);
    // Strip PDF margin markers (—HF, —LF) that pdfjs includes inside lot lines,
    // plus any stray table-header tokens ("… Article Lot Wine Bottles Price").
    const nameWithPackaging = m[3]!
      .trim()
      .replace(/\s*—H?F\s*/g, ' ')
      .replace(/\s*—L?F\s*/g, ' ')
      .replace(/\s+(?:Article\s+Lot\s+Wine\s+Bottles?\s+Price|Artikel\s+Lot\s+Wein\s+Anzahl\s+Preis)\s*/gi, ' ')
      .trim();
    const qty        = parseInt(m[4]!);
    const lotPriceEur = parseEur(m[6]!);

    // Packaging tokens can land either in the regex's packaging slot (same line)
    // or spliced into the wine name (wrapped line). Collect from both.
    const fromName = extractPackaging(nameWithPackaging);
    const fromSlot = extractPackaging(m[5] ?? '');
    const packaging = [...fromName.tokens, ...fromSlot.tokens];
    const rawWineName = fromName.clean;

    const parsed = parseWineName(rawWineName);
    if (!parsed) { unparsedLines.push(m[0]); continue; }

    // Article numbers are unique per lot on an MWC invoice — guard against the
    // same physical line being matched twice by a future regex change.
    if (seenArticles.has(article)) continue;
    seenArticles.add(article);

    const producerName = normalizeProducerName(parsed.producer);
    const style = inferStyle(parsed.cuvee, producerName);

    lots.push({
      article,
      lot,
      rawWineName,
      qty,
      inOwc: packaging.some(isOwcToken),
      inGiftBox: packaging.some(isGiftToken),
      lotPriceEur,
      pricePerBottleEur: 0, // filled in by allocateLandedCosts() below
      producerRaw: parsed.producer,
      producerName,
      year: parsed.year,
      cuvee: parsed.cuvee,
      inferredStyle: style,
      inferredAppellation: inferAppellation(parsed.cuvee, { producerName, style }),
    });
  }

  allocateLandedCosts(lots, totals);

  return { lots, meta, totals, reconciliation: reconcile(lots, totals), unparsedLines };
}

// ---------------------------------------------------------------------------
// Landed-cost allocation
// ---------------------------------------------------------------------------

/**
 * Spread the invoice's actual fees across the parsed lots, in place.
 *
 * Each fee is allocated on the basis it is actually charged on, so the sum of
 * `pricePerBottleEur × qty` reconciles to the invoice "Billing Sum":
 *   buyer's premium  → pro-rata by hammer value  (it IS a % of hammer)
 *   lot fee          → per LOT, split over that lot's bottles
 *   shipping + assurance → per bottle
 *   VAT              → applied to every fee component (never to hammer —
 *                      MWC bills the goods under margin/CDR)
 *
 * Falls back to the legacy ×1.1785 + €5-per-lot formula when the totals block
 * could not be read.
 */
function allocateLandedCosts(lots: ParsedLot[], t: InvoiceTotals): void {
  if (lots.length === 0) return;

  const totalQty = lots.reduce((s, l) => s + l.qty, 0);
  const vatRate = t.vatRate ?? 0.19;
  const premiumRate = t.premiumRate ?? 0.15;
  // Lot fee: stated per lot; "0 x Lot fee 0,00 €" (waived) → 0, not the €5 fallback.
  const perLotFee = t.lotFeeTotal != null && t.lotCount
    ? t.lotFeeTotal / t.lotCount
    : t.lotFeeTotal === 0 ? 0 : 5;
  // Shipping, assurance and any other fee lines (handling, auction fee, credits)
  // are charged per shipment → spread per bottle.
  const perBottleLogistics = totalQty > 0
    ? ((t.shipping ?? 0) + (t.assurance ?? 0) + t.otherFees) / totalQty
    : 0;

  for (const l of lots) {
    const premium   = l.lotPriceEur * premiumRate;
    const feeBase   = premium + perLotFee + perBottleLogistics * l.qty;
    const lotLanded = l.lotPriceEur + feeBase * (1 + vatRate);
    l.pricePerBottleEur = round2(lotLanded / l.qty);
  }
}

// ---------------------------------------------------------------------------
// Invoice reconciliation
// ---------------------------------------------------------------------------

/** €0.02 absorbs MWC's own per-line rounding; per-bottle rounding scales with qty. */
const EUR_TOL = 0.02;

export function mk(
  id: string,
  label: string,
  invoice: number | null,
  computed: number,
  severity: 'error' | 'warning',
  unit: 'count' | 'eur' | 'money',
  tol: number,
  hint?: string
): ReconCheck {
  const delta = invoice == null ? 0 : round2(computed - invoice);
  return {
    id, label, invoice, computed: unit === 'count' ? computed : round2(computed),
    delta,
    // A field we could not read is reported, but never fails the import.
    ok: invoice == null ? true : Math.abs(computed - invoice) <= tol,
    severity, unit, hint,
  };
}

/**
 * Cross-check the parsed lots against the invoice's own totals block. Two
 * independent proofs that nothing was dropped:
 *   1. lot count   — the "N x Lot fee" line states N explicitly
 *   2. hammer sum  — Σ lot prices must equal "Hammer total"
 * Plus arithmetic self-checks (premium, netto fees, VAT, billing sum) that
 * catch a mis-read totals block, and a landed-cost tie-out to the Billing Sum.
 */
export function reconcile(lots: ParsedLot[], t: InvoiceTotals): Reconciliation {
  const checks: ReconCheck[] = [];

  const hammerSum = round2(lots.reduce((s, l) => s + l.lotPriceEur, 0));
  const totalQty  = lots.reduce((s, l) => s + l.qty, 0);

  checks.push(mk(
    'lot_count', 'Lots parsed vs "N × Lot fee"',
    t.lotCount, lots.length, 'error', 'count', 0,
    'The invoice states its own lot count. A mismatch means lines were dropped by the parser.'
  ));

  checks.push(mk(
    'hammer', 'Σ hammer prices vs "Hammer total"',
    t.hammerTotal, hammerSum, 'error', 'eur', EUR_TOL,
    'Independent proof that every lot line was captured with the right price.'
  ));

  if (t.hammerTotal != null && t.premiumRate != null) {
    checks.push(mk(
      'premium', `${(t.premiumRate * 100).toFixed(0)}% buyer's premium`,
      t.premiumTotal, t.hammerTotal * t.premiumRate, 'warning', 'eur', EUR_TOL
    ));
  }

  const nettoCalc =
    (t.premiumTotal ?? 0) + (t.lotFeeTotal ?? 0) + (t.shipping ?? 0) + (t.assurance ?? 0) + t.otherFees;
  checks.push(mk(
    'netto_fees', t.otherFees
      ? `Premium + lot fees + shipping + assurance + other (${t.otherFeeLines.join('; ')})`
      : 'Premium + lot fees + shipping + assurance',
    t.nettoFees, nettoCalc, 'warning', 'eur', EUR_TOL
  ));

  if (t.vatRate != null && t.nettoFees != null) {
    checks.push(mk(
      'vat', `${(t.vatRate * 100).toFixed(0)}% VAT on net fees`,
      t.vatTotal, t.nettoFees * t.vatRate, 'warning', 'eur', EUR_TOL,
      'VAT is charged on the fees only — the goods are billed under margin scheme / CDR.'
    ));
  }

  checks.push(mk(
    'fees_total', 'Net fees + VAT',
    t.feesTotal, (t.nettoFees ?? 0) + (t.vatTotal ?? 0), 'warning', 'eur', EUR_TOL
  ));

  checks.push(mk(
    'billing_sum', 'Hammer total + fees total',
    t.billingSum, (t.hammerTotal ?? 0) + (t.feesTotal ?? 0), 'warning', 'eur', EUR_TOL
  ));

  // Tie-out: does the cost basis we are about to write to every bottle add back
  // up to what was actually paid? Tolerance scales with per-bottle rounding.
  const landedSum = round2(lots.reduce((s, l) => s + l.pricePerBottleEur * l.qty, 0));
  checks.push(mk(
    'landed', 'Σ allocated landed cost vs "Billing Sum"',
    t.billingSum, landedSum, 'warning', 'eur', Math.max(EUR_TOL, totalQty * 0.01),
    'Rounding each bottle to the cent can drift by up to €0.01 per bottle.'
  ));

  return {
    checks,
    hasErrors:   checks.some(c => !c.ok && c.severity === 'error'),
    hasWarnings: checks.some(c => !c.ok && c.severity === 'warning'),
  };
}
